<template>
    <div class="main-sections">
        <div class="header-content">
            <h3 class="h2 font-weight-bold title">Who played for these clubs?</h3>
            <button type="button" class="btn btn-danger finish-button" @click="finishGame(true)"
                :disabled="!allPlayersViewed">
                Finish Game
            </button>
        </div>

        <div class="game-elements">
            <!-- Active game elements -->
            <div v-if="activeGame" class="content-container">
                <div class="inner-container">

                    <div class="player-card-container">
                        <transition name="fade-slide-up">
                            <PlayerCard v-if="shouldShowPlayerCard" :selectedGamePlayer="selectedGamePlayer" />
                        </transition>

                    </div>
                </div>

                <div class="player-history-container" ref="scrollContainer" @click="onPlayerHistoryClick">
                    <PlayerHistory :transfers="selectedGamePlayer.player.transfers"
                        :class="{ 'blurred': shouldShowPlayerCard }" />
                </div>

            </div>
            <!-- Inactive game elements -->
            <h3 v-if="!activeGame">Loading...</h3>
        </div>

        <!-- Navigation -->
        <section v-if="activeGame" class="section section-components">
            <!-- Conditional rendering to display input button wrapper or guessOptions buttons -->
            <div v-if="guessOptions.length === 0" class="input-button-wrapper"
                :class="{ 'shake-animation': shakeAnimation }">
                <base-input ref="playerInputRef" class="player-input" placeholder="Player name" v-model="userInput"
                    @input="handleInputChange"></base-input>
                <button type="button" class="btn btn-success" @click="handleEnterClicked">
                    {{ isPlayerGuessed ? "Next" : "Try" }}
                </button>
            </div>


            <div v-else class="guess-option-buttons-container">
                <!-- Display buttons for each guessOption value -->
                <button v-for="(option, index) in guessOptions" :key="index" type="button"
                    class="btn guess-option-button" :class="{
                        'btn-success': isPlayerGuessed && playerGuesses[selectedPlayerIndex] === option, // Change to your logic for determining correctness
                        'btn-warning': !isPlayerGuessed && playerGuesses[selectedPlayerIndex] === option && !isPendingGuessResult, // Adjust based on your correctness logic
                        'btn-secondary': !isPlayerGuessed || playerGuesses[selectedPlayerIndex] !== option
                    }" @click="sendMultipleChoiceGuess(option)"
                    :disabled="isPendingGuessResult || playerGuesses[selectedPlayerIndex] != null">
                    {{ option }}
                </button>
            </div>


            <!-- Bottom nav viewpager -->
            <div class="viewPager">
                <div class="arrow bg-gradient-primary icon icon-shape rounded-circle text-white"
                    :class="{ 'disabled': selectedPlayerIndex <= 0 }" @click="changePlayer(-1)">
                    <i class="ni ni-bold-left"></i>
                </div>

                <div class="player-selector">
                    <div class="player-option" v-for="(player, index) in activeGame.players" :key="index"
                        @click="setSelectedPlayer(index)" :class="{
                            'attempted': viewedPlayers.has(index),
                            'active': selectedPlayerIndex === index,
                            'guessed': player.status === 'GUESSED',
                            'wrong': player.status === 'ATTEMPTED',
                        }">
                        <span class="player-number">{{ index + 1 }}</span>
                    </div>
                </div>

                <div class="arrow bg-gradient-primary icon icon-shape rounded-circle text-white"
                    :class="{ 'disabled': selectedPlayerIndex >= activeGame.players.length - 1 }"
                    @click="changePlayer(1)">
                    <i class="ni ni-bold-right"></i>
                </div>
            </div>

        </section>
    </div>
</template>

<script>
import PlayerHistory from './components/PlayerHistory.vue';
import PlayerCard from './components/PlayerCard.vue';
import axios from 'axios';
import _ from 'lodash';

export default {
    props: ['difficulty'],
    components: {
        PlayerHistory,
        PlayerCard
    },
    data() {
        return {
            apiBaseUrl: process.env.VUE_APP_API_BASE_URL,
            selectedGamePlayer: null,
            selectedPlayerIndex: 0,
            activeGame: null,
            userInput: '',
            debouncedSendGuess: null,
            viewedPlayers: new Set(),
            shakeAnimation: false,
            playerGuesses: [],
            isPendingGuessResult: false,
        };
    },
    created() {
        this.debouncedSendGuess = _.debounce(this.sendGuess, 500);
        console.log("Checking for active game.");
        this.checkForActiveGame();
    },
    mounted() {
        this.updateSelectedGamePlayer();
        window.addEventListener('keyup', this.handleKeyPress);
        this.applyMobileSpecificStyles();
    },
    beforeDestroy() {
        // Remove event listener
        window.removeEventListener('keyup', this.handleKeyPress);
    },
    computed: {
        isPlayerGuessed() {
            const currentPlayer = this.activeGame.players[this.selectedPlayerIndex];
            return currentPlayer && currentPlayer.status === 'GUESSED';
        },
        allPlayersViewed() {
            return this.activeGame && this.viewedPlayers.size === this.activeGame.players.length;
        },
        guessOptions() {
            const currentPlayer = this.activeGame.players[this.selectedPlayerIndex];
            if (currentPlayer && currentPlayer.guessOptions) {
                return currentPlayer.guessOptions;
            }
            return [];
        },
        shouldShowPlayerCard() {
            const currentPlayer = this.activeGame.players[this.selectedPlayerIndex];

            return this.isPlayerGuessed || (currentPlayer.status === 'ATTEMPTED' && currentPlayer.guessOptions)
        }
    },
    methods: {
        applyMobileSpecificStyles() {
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            if (isMobile) {
                const style = document.createElement('style');
                style.type = 'text/css';
                style.innerHTML = `
          .game-elements {
            justify-content: start;
            padding-top: 2em;
          }
        `;
                document.head.appendChild(style);
            }
        },
        async checkForActiveGame() {
            const savedgameUuid = localStorage.getItem('gameUuid');

            if (savedgameUuid) {
                console.log("Trying to get game", savedgameUuid)
                try {
                    const response = await axios.get(`${this.apiBaseUrl}/api/get_game/${savedgameUuid}/`);
                    if (response.data.status === 'ACTIVE') {
                        this.activeGame = response.data;
                        this.updatePlayerCountVariable();
                        this.updateSelectedGamePlayer();
                        this.playerGuesses = new Array(this.activeGame.players.length).fill(null);
                    } else {
                        this.activeGame = null;
                        this.startNewGame();
                    }
                } catch (error) {
                    console.error("An error occurred:", error);
                }
            } else {
                this.startNewGame();
            }
        },
        async startNewGame(difficulty = null) {
            try {
                let payload = {};

                if (difficulty !== null) {
                    payload.difficulty = difficulty;
                }

                const response = await axios.post(`${this.apiBaseUrl}/api/create_game/`, payload);
                this.activeGame = response.data;
                this.playerGuesses = new Array(this.activeGame.players.length).fill(null);
                this.updatePlayerCountVariable();
                localStorage.setItem('gameUuid', response.data.uuid);
                this.selectedPlayerIndex = 0;
                this.userInput = '';
                this.updateSelectedGamePlayer();

                if (typeof gtag !== "undefined") {
                    gtag('event', 'startedNewGame', {
                        uuid: response.data.uuid,
                        difficulty: difficulty || 'NORMAL'
                    });
                }
            } catch (error) {
                console.error("An error occurred:", error);
            }
        },
        async finishGame(manually = false) {
            try {
                const response = await axios.put(`${this.apiBaseUrl}/api/game/${this.activeGame.uuid}/finish/`)

                if (typeof gtag !== "undefined") {
                    gtag('event', 'finishGame', {
                        manually: manually
                    });
                }

                if (response.status === 200) {
                    this.$router.push({ name: 'summary', params: { gameUuid: this.activeGame.uuid } });
                } else {
                    console.log("Failed to finish game:", response);
                }
            } catch (error) {
                console.error("An error occurred while finishing the game:", error);
            }
        },
        updatePlayerCountVariable() {
            // Assuming `this.activeGame.players` holds the array of players
            const playerCount = this.activeGame.players.length;
            document.documentElement.style.setProperty('--player-count', playerCount);
        },
        updateSelectedGamePlayer() {
            if (this.activeGame && this.activeGame.players[this.selectedPlayerIndex]) {
                this.selectedGamePlayer = this.activeGame.players[this.selectedPlayerIndex];

                this.$nextTick(() => {
                    if (this.$refs.playerInputRef) {
                        this.$refs.playerInputRef.$el.querySelector('input').focus();
                        this.userInput = '';
                    }

                    if (!this.isPlayerGuessed) {
                        this.scrollToEnd()
                    }
                });
                this.viewedPlayers.add(this.selectedPlayerIndex);
                this.viewedPlayers = new Set(this.viewedPlayers);
            }
        },
        setSelectedPlayer(index) {
            if (index >= 0 && index < this.activeGame.players.length) {
                this.selectedPlayerIndex = index;
                this.updateSelectedGamePlayer();
            }
        },
        changePlayer(direction) {
            // Check if the left arrow is clicked and the current index is 0 or if the right arrow is clicked and the current index is at the end.
            if ((direction < 0 && this.selectedPlayerIndex <= 0) || (direction > 0 && this.selectedPlayerIndex >= this.activeGame.players.length - 1)) {
                // The operation is invalid in this context (equivalent to clicking a disabled arrow), so do nothing.
                return;
            }

            // Update the selected player index if the arrow is enabled.
            this.selectedPlayerIndex += direction;
            this.updateSelectedGamePlayer();
        },
        handleEnterClicked() {
            if (this.isPlayerGuessed) {
                let nextIndex = -1;
                // Start from next index
                for (let i = this.selectedPlayerIndex + 1; i < this.activeGame.players.length; i++) {
                    if (this.activeGame.players[i].status !== 'GUESSED') {
                        nextIndex = i;
                        break;
                    }
                }

                // If no player found, start from zero
                if (nextIndex === -1) {
                    for (let i = 0; i < this.selectedPlayerIndex; i++) {
                        if (this.activeGame.players[i].status !== 'GUESSED') {
                            nextIndex = i;
                            break;
                        }
                    }
                }

                // If still not found, do nothing
                if (nextIndex === -1) {
                    return;
                }

                // Set the selected player
                this.setSelectedPlayer(nextIndex);
            } else {
                this.debouncedSendGuess.cancel();
                this.sendGuess();
            }
        },
        handleKeyPress(event) {
            if (event.keyCode === 13) {  // 13 is the key code for Enter
                this.handleEnterClicked()
            } else if (event.keyCode === 39 && !this.userInput) {  // 39 is the Right Arrow key code
                let nextIndex = this.selectedPlayerIndex + 1;
                if (nextIndex >= this.activeGame.players.length) {
                    nextIndex = 0;  // Loop around if reached the end
                }
                this.setSelectedPlayer(nextIndex);
            } else if (event.keyCode === 37 && !this.userInput) {  // 37 is the Left Arrow key code
                let prevIndex = this.selectedPlayerIndex - 1;
                if (prevIndex < 0) {
                    prevIndex = this.activeGame.players.length - 1;  // Loop around if reached the beginning
                }
                this.setSelectedPlayer(prevIndex);
            }

        },
        handleInputChange() {
            if (this.userInput.length > 2 && !this.isPlayerGuessed) {
                this.debouncedSendGuess();
            }
        },
        scrollToEnd() {
            const container = this.$refs.scrollContainer;
            if (container) {
                // Instantly set scroll position to the start
                container.scrollLeft = 0;

                // Use requestAnimationFrame to ensure the instant scroll is rendered before the smooth scroll starts
                requestAnimationFrame(() => {
                    // Smooth scroll to the end
                    container.scrollTo({
                        left: container.scrollWidth, // Scroll to the end
                        behavior: 'smooth' // Enable smooth scrolling
                    });
                });
            }
        },
        sendGuess(guessValue) {
            const gameUuid = this.activeGame.uuid;
            const playerIndex = this.selectedPlayerIndex;

            this.isPendingGuessResult = true;

            // Use guessValue if provided, otherwise fall back to this.userInput.
            const guess = guessValue || this.userInput;

            axios.post(`${this.apiBaseUrl}/api/game/${gameUuid}/guess/${playerIndex}/`, {
                guess: guess
            })
                .then(response => {
                    if (typeof gtag !== "undefined") {
                    gtag('event', 'sendGuess', {
                        guess: guess,
                        guessStatus: response.data.status
                    });
                }

                    if (response.data.status === "correct") {
                        this.activeGame.players[playerIndex].player = response.data.player;
                        this.activeGame.players[playerIndex].status = "GUESSED"
                        this.userInput = ''
                    } else if (response.data.status === "incorrect_multiple_choice") {
                        this.activeGame.players[playerIndex].player = response.data.player;
                        this.activeGame.players[playerIndex].status = "ATTEMPTED"
                        this.userInput = ''
                    } else {
                        this.shakeAnimation = true;
                        setTimeout(() => this.shakeAnimation = false, 500);
                    }
                })

                .catch(error => {
                    // Handle error
                }).finally(() => {
                    this.isPendingGuessResult = false;

                    const allGuessed = this.activeGame.players.every(player => player.status === 'GUESSED');
                    const allGuessedOrAttempted = this.activeGame.players.every(player => player.status === 'GUESSED' || player.status === 'ATTEMPTED');

                    if (allGuessed || (['EASY', 'NORMAL'].includes(this.activeGame.difficulty) && allGuessedOrAttempted)) {
                        this.finishGame(false);
                    }
                });
        },
        sendMultipleChoiceGuess(guessValue) {
            console.log('sendMultipleChoiceGuess called with guessValue:', guessValue);

            if (this.isPlayerGuessed || this.playerGuesses[this.selectedPlayerIndex] != null) {
                console.log(`Player at index ${this.selectedPlayerIndex} has already guessed or has a non-null guess:`, this.playerGuesses[this.selectedPlayerIndex]);
                return;
            }

            console.log(`Setting guess for player at index ${this.selectedPlayerIndex} to:`, guessValue);
            this.$set(this.playerGuesses, this.selectedPlayerIndex, guessValue);

            // Additional log to confirm the array after assignment
            console.log('Updated playerGuesses array:', this.playerGuesses);

            this.debouncedSendGuess.cancel();
            this.sendGuess(guessValue);
        },


        setPlayerInputFocus() {
            this.$refs.playerInputRef.onFocus(true);
        },
        onPlayerHistoryClick(event) {
            // Ensure action only if the player status is 'GUESSED'
            if (!this.isPlayerGuessed) return;

            const { offsetX, target } = event;
            const halfWidth = target.offsetWidth / 2;

            if (offsetX > halfWidth) {
                let nextIndex = this.selectedPlayerIndex + 1;

                if (nextIndex >= this.activeGame.players.length) {
                    nextIndex = 0;  // Loop around if reached the end
                }

                this.setSelectedPlayer(nextIndex);
            } else {
                let prevIndex = this.selectedPlayerIndex - 1;
                if (prevIndex < 0) {
                    prevIndex = this.activeGame.players.length - 1;  // Loop around if reached the beginning
                }

                this.setSelectedPlayer(prevIndex);
            }
        }
    }
}
</script>



<style scoped>
.blurred {
    filter: blur(5px);
    pointer-events: none;
    /* Ensure it takes up the full width of the container */
}

.main-sections {
    display: flex;
    flex-direction: column;
    align-items: top;
    justify-content: top;
    flex-grow: 1;
    padding-bottom: 100px;
}

.header-content {
    margin-top: 0.5rem;
    display: flex;
    flex-direction: row;
    /* Align children horizontally */
    align-items: center;
    /* Vertical alignment */
    justify-content: center;
    /* Horizontal alignment */
    margin-bottom: 3vh;
}


.title {
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 4vw;
    line-height: 1.2;
    display: flex;
    align-items: center;
    justify-content: center;
}

.finish-button {
    white-space: nowrap;
    margin-left: 10px;
    align-self: center;
    /* This ensures the button is also centered vertically */
}


/* Large screens */
@media (min-width: 768px) {
    .title {
        font-size: 24px;
        /* Maximum font size for larger screens */
    }
}

/* Medium screens, if the font size is too large */
@media (max-width: 767px) {
    .title {
        font-size: calc(4vw + 1px);
        /* Slightly smaller font size for medium screens */
    }
}

/* Small screens, if the font size is still too large */
@media (max-width: 480px) {
    .title {
        font-size: calc(3.5vw + 1px);
        /* Even smaller font size for small screens */
    }
}

.game-elements {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

/* Media query for window height less than 900px */
@media screen and (max-height: 900px) {
    .game-elements {
        justify-content: start;
        padding-top: 2em;
    }
}


.content-container {
    /* Remove align-items: center; */
    align-self: flex-start;
    /* Align .content-container to the top */
    position: relative;
    width: 100%;
}

.inner-container {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10;
    pointer-events: none;
}

.player-history-container {
    overflow-x: auto;
    justify-content: flex-start;
    /* Align items to the start of the container */
    width: 100vw;
    padding: 2em 0.5em;
}


.header {
    width: 100%;
    height: 90px;
    margin-bottom: 2vh;
}

.input-button-wrapper {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
}

.btn-success {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
}


.player-input {
    width: 16em;
    max-width: 80vw;
    margin: 10px;
}


.section-components {

    /* Align to the bottom of the viewport */
    left: 0;
    /* Stretch across the left */
    width: 100%;
    /* Ensure it spans the full width of the viewport */
    z-index: 1000;
    /* Ensure it sits above other content */
    display: flex;
    /* Ensure this is a flex container */
    flex-direction: column;
    /* Stack children vertically */
    justify-content: center;
    align-items: center;
    padding-bottom: 5rem;
    background: linear-gradient(0deg, rgb(255, 255, 255) 0%, rgba(255, 255, 255, 0) 100%);
}



@media (max-height: 767px) {
    .section-components {
        padding-bottom: 1rem;
    }
}


.viewPager {
    display: flex;
    align-items: center;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 50em;
    padding: 0.1em 0.5em;
}

.player-selector {
    display: flex;
    flex: 1;
    /* Take the remaining space */
    margin: 0 0.5em;
    /* Adjust space around the selector as necessary */
}

.player-option {
    flex: 1;
    /* Flex each item evenly */
    width: calc(80% / var(--player-count));
    /* 80% of the viewpager width divided by the number of players */
    border: 2px solid black;
    /* Black line between items */
    text-align: center;
    /* Center text */
    cursor: pointer;
    /* Indicates the item is clickable */
    color: black;
}


.player-number {
    font-size: 2rem;
    /* Responsive font size based on viewport width */
    display: block;
}

.player-option.attempted {
    background-color: #ffd600;
}

.player-option.guessed {
    background-color: #2dce89;
    /* Background color for guessed */
    color: white;
}

.player-option.active {
    background-color: #11cdef;
    /* Background color for active */
}

.player-option.wrong {
    background-color: #fb6340;
}

/* Arrow styling */
.arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 2em;
}

.arrow.disabled {
    opacity: 0.5;
    cursor: default;
}

/* Responsive design for smaller screens */
@media (max-width: 600px) {
    .player-number {
        font-size: 4vw;
        /* Larger font size for smaller screens */
    }
}

/* Initial state */
.fade-slide-up-enter-active,
.fade-slide-up-leave-active {
    transition: opacity 0.2s ease, transform 0.5s ease;
}

/* Starting state for enter and leaving (from and to states) */
.fade-slide-up-enter,
.fade-slide-up-leave-to

/* .fade-slide-up-leave-active in <2.1.8 */
    {
    opacity: 0;
    transform: translateY(20px);
    /* Pushes the element 20px down initially */
}

.player-card-container {
    width: 40vw;
    height: 60vw;
    max-width: 32vh;
    max-height: 48vh;
    max-width: 12em;
    max-height: 18em;
}


@keyframes shake {
    0% {
        transform: translateX(0px);
    }

    25% {
        transform: translateX(-5px);
    }

    50% {
        transform: translateX(5px);
    }

    75% {
        transform: translateX(-5px);
    }

    100% {
        transform: translateX(0px);
    }
}

.shake-animation {
    animation: shake 0.5s;
    /* Adjust duration as needed */
}

/* Flex grid layout for guess options container */
/* Flex grid layout for guess options container */
.guess-option-buttons-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    width: 100vw;
    max-width: 50em;
    margin-bottom: 1em;
    padding-left: 1em;
    padding-right: 1em;
}

/* Button styling */
.guess-option-button {
    flex: 1 0 calc(50% - 1em);
    /* Flex percentage of 50% for big screens */
    margin-bottom: 1em;
    /* Adjust the vertical spacing between buttons as needed */
}

/* Media query for big screens */
@media screen and (min-width: 768px) {
    .guess-option-button {
        flex-basis: calc(50% - 1em);
        /* Flex basis of 50% for big screens */
    }
}

/* Media query for smaller screens */
@media screen and (max-width: 768px) {
    .guess-option-button {
        flex: 1 0 100%;
        /* Flex percentage of 100% for small screens */
    }
}
</style>
